import ClearIcon from "@mui/icons-material/Clear";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React from "react";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import type { ConditionText } from "@/hooks/dispatch/adhocConditions";
import { brandPrimary, zinc } from "@/ui/tokens";
import { FILTERED_ACTION_SX, FILTERED_TINT } from "./dispatchColors";

/** One entry the strip shows: what it says, where it came from, and how to take it off. */
export type QueryChip = ConditionText & {
  key: string;
  /**
   * A control chip switches its control off; an ad-hoc chip removes the condition; a display chip (a sort the member
   * set over the view's) puts the view's own back.
   */
  source: "control" | "adhoc" | "display";
  onClear: () => void;
};

const CHIP_BORDER: Record<QueryChip["source"], string> = {
  control: brandPrimary[400],
  adhoc: brandPrimary[400],
  display: zinc[500],
};

/**
 * The line under the header that says why the list reads as it does: one chip per active control, ad-hoc condition,
 * and sort the member set over the view's, condensed with the detail on hover, each clearable, plus a clear-all. While
 * anything hides rows it is a tinted banner naming how many, so a filter left on cannot pass for missing drivers; a sort
 * alone keeps the muted line. While a search sets the filters aside it shrinks to one grey line naming the filters it
 * hides, so the search reads as the only thing in force. It displays and clears; conditions are added from the controls
 * and the row menu, never edited here.
 */
export default React.memo(function DispatchQueryStrip({
  chips,
  onClearAll,
  filterCount,
  hiddenCount,
  noun,
  searching,
  onClearSearch,
}: {
  chips: readonly QueryChip[];
  onClearAll: () => void;
  /** How many chips hide rows; a sort alone hides none. */
  filterCount: number;
  /** How many rows the filters hide. */
  hiddenCount: number;
  /** What a row is, singular: "driver" or "message". */
  noun: string;
  /** Whether a search has set the filters aside. */
  searching: boolean;
  onClearSearch: () => void;
}) {
  if (searching) {
    return filterCount > 0 ? (
      <HiddenFiltersLine chips={chips} filterCount={filterCount} onClearSearch={onClearSearch} />
    ) : null;
  }
  if (chips.length === 0) return null;
  const filtered = filterCount > 0;
  return (
    <Box
      data-sb={TestIds.dispatch.queryStrip}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        flexWrap: "wrap",
        ...(filtered
          ? {
              mx: 2,
              mb: 1,
              pl: 1.25,
              pr: 0.75,
              py: 0.5,
              borderRadius: 1,
              bgcolor: FILTERED_TINT,
              border: `1px solid color-mix(in oklch, ${brandPrimary[400]} 35%, transparent)`,
            }
          : { px: 2, pb: 0.75 }),
      }}
    >
      {filtered ? (
        <>
          <Typography
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: "0.75rem",
              fontWeight: 600,
              color: brandPrimary[200],
            }}
          >
            <FilterAltIcon sx={{ fontSize: 14 }} />
            Filtered
          </Typography>
          {hiddenCount > 0 && (
            <Typography sx={{ fontSize: "0.75rem", color: brandPrimary[300], mr: 0.5 }}>
              · {hiddenCount} {hiddenCount === 1 ? noun : `${noun}s`} hidden
            </Typography>
          )}
        </>
      ) : (
        <Typography
          sx={{ fontSize: "0.6875rem", color: zinc[500], textTransform: "uppercase", letterSpacing: "0.04em" }}
        >
          Showing
        </Typography>
      )}
      {chips.map((chip) => (
        <Tooltip key={chip.key} title={chip.detail} enterDelay={400} enterNextDelay={400}>
          <Box
            data-sb={TestIds.dispatch.queryChip}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.5,
              height: 22,
              pl: 1,
              pr: 0.25,
              borderRadius: 11,
              fontSize: "0.75rem",
              color: filtered ? zinc[200] : zinc[300],
              bgcolor: filtered ? zinc[900] : zinc[800],
              border: `1px solid ${CHIP_BORDER[chip.source]}`,
            }}
          >
            {chip.summary}
            <ButtonBase
              aria-label={`Clear ${chip.summary}`}
              onClick={chip.onClear}
              sx={{ borderRadius: "50%", p: 0.25, color: zinc[500], "&:hover": { color: "white" } }}
            >
              <ClearIcon sx={{ fontSize: 14 }} />
            </ButtonBase>
          </Box>
        </Tooltip>
      ))}
      <ButtonBase
        data-sb={TestIds.dispatch.queryClearAll}
        onClick={onClearAll}
        sx={
          filtered
            ? {
                ml: "auto",
                height: 24,
                px: 1.25,
                borderRadius: 1,
                fontSize: "0.75rem",
                fontWeight: 600,
                ...FILTERED_ACTION_SX,
              }
            : { fontSize: "0.75rem", color: zinc[500], px: 0.5, borderRadius: 1, "&:hover": { color: "white" } }
        }
      >
        {filtered ? "Clear all filters" : "Clear all"}
      </ButtonBase>
    </Box>
  );
});

/** The strip while a search is in force: the filters it sets aside, greyed, and the way back to them. */
function HiddenFiltersLine({
  chips,
  filterCount,
  onClearSearch,
}: {
  chips: readonly QueryChip[];
  filterCount: number;
  onClearSearch: () => void;
}) {
  const hidden = chips
    .filter((chip) => chip.source !== "display")
    .map((chip) => chip.summary)
    .join(" · ");
  return (
    <Box
      data-sb={TestIds.dispatch.queryStrip}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        mx: 2,
        mb: 1,
        px: 1.25,
        py: 0.5,
        borderRadius: 1,
        border: `1px dashed ${zinc[700]}`,
      }}
    >
      <Typography
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          fontSize: "0.6875rem",
          fontWeight: 600,
          color: zinc[400],
          textTransform: "uppercase",
          letterSpacing: "0.04em",
        }}
      >
        <VisibilityOffIcon sx={{ fontSize: 14 }} />
        {filterCount} {filterCount === 1 ? "filter" : "filters"} hidden
      </Typography>
      <Typography sx={{ fontSize: "0.75rem", color: zinc[500] }}>{hidden}</Typography>
      <ButtonBase
        onClick={onClearSearch}
        sx={{
          ml: "auto",
          fontSize: "0.75rem",
          color: zinc[400],
          px: 0.5,
          borderRadius: 1,
          "&:hover": { color: "white" },
        }}
      >
        Clear search to unhide filters
      </ButtonBase>
    </Box>
  );
}
